import React, { useState } from "react";
import { Box, Collapse, styled } from "@mui/material";
import { H3, P1, typographyStyles } from "./Typography";

export interface FAQItem {
    question: string;
    answer?: string;
    answerWithList?: {
        text: string;
        items: string[];
    };
}

const Section = styled(Box)(() => ({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "20px",
    "@media (max-width: 600px)": {
        gap: "16px",
    },
}));

const AccordionItem = styled(Box)(() => ({
    width: "100%",
    backgroundColor: "transparent",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    "@media (max-width: 600px)": {
        gap: "12px",
    },
}));

const Separator = styled(Box)(() => ({
    width: "100%",
    height: "1px",
    backgroundColor: "#EBEBEB",
}));

const AccordionHeader = styled(Box)(() => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    userSelect: "none",
    gap: "16px", // Add gap between text and arrow
}));

const Question = styled(H3)(() => ({
    color: "var(--text-primary)",
    flex: 1,
    "@media (max-width: 600px)": {
        fontSize: "18px",
        lineHeight: "24px",
    },
}));

const ArrowIcon = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isOpen",
})<{ isOpen: boolean }>(({ isOpen }) => ({
    width: "24px",
    height: "24px",
    minWidth: "24px", // Prevent shrinking
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform 0.2s ease, opacity 0.2s ease",
    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
    "&:hover": {
        opacity: 0.7,
    },
    "& svg": {
        width: "100%",
        height: "100%",
    },
}));

const Answer = styled(P1)(() => ({
    color: "var(--text-tertiary)",
    marginBottom: "8px",
    "@media (max-width: 600px)": {
        fontSize: "16px",
        lineHeight: "24px",
    },
}));

const BulletList = styled("ul")(() => ({
    margin: "8px 0 0 0",
    padding: "0 0 0 20px",
    listStyleType: "disc",
    "& li": {
        ...typographyStyles.p1,
        color: "var(--text-tertiary)",
        marginBottom: "4px",
        "@media (max-width: 600px)": {
            fontSize: "16px",
            lineHeight: "24px",
        },
    },
}));

const defaultFaqItems: FAQItem[] = [
    {
        question: "Что такое Сфера Кошелек?",
        answer: "Это платформа, работающая по принципу «Единого окна» для управления международными финансами.",
    },
    {
        question: "Какие возможности дает сервис?",
        answerWithList: {
            text: "Мы объединили четыре ключевые функции в одном продукте:",
            items: [
                "Банковское обслуживание",
                "Платежная система",
                "Брокерские услуги",
                "Консалтинг",
            ],
        },
    },
    {
        question: "Нужно ли регистрироваться на разных платформах для доступа к услугам?",
        answer: "Нет, все инструменты (от банкинга до консалтинга) доступны в едином интерфейсе.",
    },
];

interface FAQSectionProps {
    items?: FAQItem[];
    defaultOpen?: number[];
}

export const FAQSection = ({ items = defaultFaqItems, defaultOpen }: FAQSectionProps) => {
    const [openItems, setOpenItems] = useState<number[]>(
        defaultOpen ?? items.map((_, i) => i)
    );

    const toggleItem = (index: number) => {
        setOpenItems((prev) =>
            prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
        );
    };

    return (
        <Section>
            {items.map((item, index) => {
                const isOpen = openItems.includes(index);
                const isLast = index === items.length - 1;
                return (
                    <React.Fragment key={index}>
                        <AccordionItem>
                            <AccordionHeader onClick={() => toggleItem(index)}>
                                <Question>{item.question}</Question>
                                <ArrowIcon isOpen={isOpen}>
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M6 9L12 15L18 9"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </ArrowIcon>
                            </AccordionHeader>
                            <Collapse in={isOpen}>
                                <Box>
                                    {item.answer && <Answer>{item.answer}</Answer>}
                                    {item.answerWithList && (
                                        <>
                                            <Answer>{item.answerWithList.text}</Answer>
                                            <BulletList>
                                                {item.answerWithList.items.map((listItem, i) => (
                                                    <li key={i}>{listItem}</li>
                                                ))}
                                            </BulletList>
                                        </>
                                    )}
                                </Box>
                            </Collapse>
                        </AccordionItem>
                        {!isLast && <Separator />}
                    </React.Fragment>
                );
            })}
        </Section>
    );
};
